import React from 'react';
import { StyleSheet, View } from 'react-native';

import { BigButton } from '@components/Button';
import Icon from '@components/Icon';
import ScreenContainer from '@components/ScreenContainer';
import Switch from '@components/Switch';
import { Caption, Paragraph, PrimaryHeadline, Title } from '@components/Typography';
import { useContacts } from '@context/ContactsContext';
import { useNotifications } from '@context/NotificationsContext';
import { requestForegroundLocationPermissions } from '@helpers/Location';
import { logError } from '@helpers/Loggers';
import { OnboardingRoute } from '@navigation/Routes';
import { RequestPermissionsScreenProps } from '@navigation/Types';

export const NOTIFICATIONS_PERMISSION_CAPTION =
  "We send notifications to help you stay in the know about friends' plans, direct messages, and more.";
export const CONTACTS_PERMISSION_CAPTION =
  'We upload your contacts to our servers so we can suggest friends to follow and notify you when someone you know joins.';
export const LOCATION_PERMISSION_CAPTION =
  'We use your location to suggest plans happening near your current city.';
export const PERMISSIONS_INTRO = "Choose which features you'd like to enable.";

export default function RequestPermissionsScreen({ navigation }: RequestPermissionsScreenProps) {
  const { requestPermissionAndSync: requestContactsPermissionAndSync } = useContacts();
  const { requestPermissionAndSync: requestNotificationsPermissionAndSync } = useNotifications();
  const [notificationsChecked, setNotificationsChecked] = React.useState<boolean>(true);
  const [contactsChecked, setContactsChecked] = React.useState<boolean>(true);
  const [locationChecked, setLocationChecked] = React.useState<boolean>(true);

  const renderOption = (
    icon: React.ComponentProps<typeof Icon>['name'],
    title: string,
    caption: string,
    switchValue: boolean,
    switchOnValueChange: () => void,
    switchTestID: string
  ) => (
    <View style={styles.optionContainer}>
      <View style={styles.row}>
        <Icon name={icon} style={styles.icon} />
        <View style={styles.textContainer}>
          <Title>{title}</Title>
          <Caption>{caption}</Caption>
        </View>
        <Switch
          value={switchValue}
          onValueChange={switchOnValueChange}
          style={styles.switch}
          testID={switchTestID}
        />
      </View>
    </View>
  );

  const renderNotificationOption = () =>
    renderOption(
      'bell-outline',
      'Notifications',
      NOTIFICATIONS_PERMISSION_CAPTION,
      notificationsChecked,
      () => setNotificationsChecked(!notificationsChecked),
      'screens.RequestPermissionsScreen.Switch.Notifications'
    );

  const renderContactsOption = () =>
    renderOption(
      'account-multiple-outline',
      'Contacts',
      CONTACTS_PERMISSION_CAPTION,
      contactsChecked,
      () => setContactsChecked(!contactsChecked),
      'screens.RequestPermissionsScreen.Switch.Contacts'
    );

  const renderLocationOption = () =>
    renderOption(
      'map-marker-outline',
      'Location',
      LOCATION_PERMISSION_CAPTION,
      locationChecked,
      () => setLocationChecked(!locationChecked),
      'screens.RequestPermissionsScreen.Switch.Location'
    );

  const onPress = async () => {
    if (notificationsChecked) {
      try {
        await requestNotificationsPermissionAndSync();
      } catch (error) {
        logError(error);
      }
    }
    if (contactsChecked) {
      try {
        await requestContactsPermissionAndSync();
      } catch (error) {
        logError(error);
      }
    }
    if (locationChecked) {
      try {
        await requestForegroundLocationPermissions();
      } catch (error) {
        logError(error);
      }
    }
    navigation.navigate(OnboardingRoute.FollowUsers, { screenType: 'onboarding' });
  };

  return (
    <ScreenContainer scrollable>
      <PrimaryHeadline style={styles.headline}>Permissions</PrimaryHeadline>
      <Paragraph style={styles.paragraph}>{PERMISSIONS_INTRO}</Paragraph>
      <View style={styles.container}>
        {renderNotificationOption()}
        {renderContactsOption()}
        {renderLocationOption()}
      </View>
      <BigButton
        mode="contained"
        style={styles.button}
        onPress={onPress}
        testID="screens.RequestPermissionsScreen.Button.Continue">
        Continue
      </BigButton>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  button: {
    alignSelf: 'center',
    marginTop: 10,
    width: '90%',
  },
  container: {
    marginVertical: 10,
  },
  headline: {
    textAlign: 'center',
  },
  icon: {
    margin: 10,
  },
  optionContainer: {
    paddingVertical: 5,
  },
  paragraph: {
    padding: 10,
    paddingHorizontal: 20,
    textAlign: 'center',
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  switch: {
    margin: 10,
  },
  textContainer: {
    flex: 1,
  },
});
